import { Component, Host, h, Prop, State, Event, EventEmitter, Element, Fragment } from '@stencil/core';

import { Comment } from '../../ui-cms-blog-comment-section';
import store from '../../store';
import { deleteOrDeactivateComment } from '../../fetch-comments';

@Component({
  tag: 'ui-cms-blog-comment-content',
  styleUrl: 'ui-cms-blog-comment-content.scss',
  shadow: false,
})
export class UiCmsBlogCommentContent {
  @Element() el: HTMLElement;

  @Prop() commentData: Comment;

  @State() replyBtnTitle = 'Antworten';

  @State() replyBtnIcon = 'speech-bubble';

  @State() showReplyForm = false;

  @Prop() replyParentName = '';

  @State() clickedCommentId: string;

  @State() confirmationMessage = '';

  @Event() showMainCommentForm: EventEmitter<{ show: boolean }>;

  private getLocale() {
    const htmlLang = document.documentElement.lang;
    const locale = new Intl.Locale(htmlLang);

    return locale;
  }

  // API returns boolean values as a string, so we need to convert them back to boolean
  private convertStringToBoolean(value: string): boolean | null {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  private toggleReplyForm() {
    this.updateReplyBtnInfo();
    // toggle add comment form
    this.showReplyForm = !this.showReplyForm;
    this.clickedCommentId = this.commentData.commentId;
    this.updateActiveCommentForm();
  }

  private updateActiveCommentForm() {
    if (store.state.activeComments.includes(this.commentData.commentId)) {
      // remove comment id from store
      store.state.activeComments = store.state.activeComments.filter(
        (commentId) => commentId !== this.commentData.commentId
      );
    } else {
      // add comment id to store
      store.state.activeComments = [...store.state.activeComments, this.commentData.commentId];
    }

    // close main comment form if any reply form is open
    // emitting event since store state is not reactive
    this.showMainCommentForm.emit({ show: store.state.activeComments.length === 0 });
  }

  private updateReplyBtnInfo() {
    this.replyBtnTitle =
      this.replyBtnTitle === store.state.i18n.reply ? store.state.i18n.close : store.state.i18n.reply;
    this.replyBtnIcon = this.replyBtnIcon === 'speech-bubble' ? 'close' : 'speech-bubble';
  }

  private async deleteOrDeactivateComment(action: 'DELETE' | 'DEACTIVATE') {
    const response = await deleteOrDeactivateComment({
      apiUrl: action === 'DELETE' ? store.state.deleteCommentApiUrl : store.state.deactivateCommentApiUrl,
      commentParams: {
        commentId: this.commentData.commentId,
      },
    });

    // response format is { status: 'success' } for deactivate and { comment: 'deleted' } for delete
    if (response.status === 'success' || response.comment === 'deleted') {
      this.confirmationMessage =
        action === 'DELETE' ? store.state.i18n.commentDeleted : store.state.i18n.commentDeactivated;
    }
  }

  private decodeHtml(html: string): string {
    const txt = new DOMParser().parseFromString(html, 'text/html');
    return txt.body.textContent;
  }

  render() {
    return (
      <Host
        id={this.commentData.commentId}
        class={{
          active: this.showReplyForm,
        }}
      >
        {this.confirmationMessage ? (
          <ui-alert variant="success">{this.confirmationMessage}</ui-alert>
        ) : (
          <Fragment>
            <div class="comment-icon">
              {this.convertStringToBoolean(this.commentData.isManagement) ? (
                <img
                  src="https://global-resources.fe.union-investment.de/latest/assets/img/logos/union-investment/union-investment-mobile.svg"
                  alt=""
                />
              ) : (
                <ui-icon type="content" icon="person"></ui-icon>
              )}
            </div>
            <article class="comment-info" aria-labelledby="comment-info__name">
              <div class="comment-info__name" id="comment-info__name">
                <strong>{this.decodeHtml(this.commentData.name)}</strong>
                <span class="date">{new Date(this.commentData.date).toLocaleDateString(this.getLocale())}</span>
              </div>
              {this.replyParentName !== '' && (
                <div class="comment-info__reply-to">
                  {store.state.i18n.replyToCommentFrom} {this.replyParentName}
                </div>
              )}
              <p class="sd-paragraph comment-info__message">{this.decodeHtml(this.commentData.message)}</p>
              <div
                class={{
                  'buttons-container': true,
                  'is-editor': store.state.isEditMode,
                }}
              >
                {store.state.isEditMode && <strong>{store.state.i18n.comment}</strong>}
                <sd-button variant="tertiary" onClick={() => this.toggleReplyForm()}>
                  <sd-icon
                    library={window.ui.cmsState.iconLibrary}
                    name={`union-investment/system/${this.replyBtnIcon}`}
                    slot="icon-left"
                  ></sd-icon>
                  {this.replyBtnTitle}
                </sd-button>
                {store.state.isEditMode && (
                  <div class="editor-controls">
                    <sd-button variant="tertiary" onClick={() => this.deleteOrDeactivateComment('DEACTIVATE')}>
                      <sd-icon
                        library={window.ui.cmsState.iconLibrary}
                        name="union-investment/system/close-circle"
                        slot="icon-left"
                      ></sd-icon>
                      {store.state.i18n.deactivateComment}
                    </sd-button>
                    <sd-button variant="tertiary" onClick={() => this.deleteOrDeactivateComment('DELETE')}>
                      <sd-icon
                        library={window.ui.cmsState.iconLibrary}
                        name="union-investment/system/trash"
                        slot="icon-left"
                      ></sd-icon>
                      {store.state.i18n.deleteComment}
                    </sd-button>
                  </div>
                )}
              </div>
              {this.showReplyForm && <ui-cms-add-comment parentCommentId={this.clickedCommentId}></ui-cms-add-comment>}
            </article>
          </Fragment>
        )}
      </Host>
    );
  }
}
